/**
 * Pure roster orchestration for Manage Investors — the canonical filter/sort/stat
 * logic behind the v2 roster (`DashboardV2`). Kept pure (no component imports) so
 * the filtered/sorted/aggregated view stays unit-testable in isolation.
 */
import { Investor, InvestorFilters, EpoRule, EpdRule } from '@/types/investor';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { getInvestorProducts } from '@/utils/investor/productUtils';
import { isRuleConfigured } from '@/utils/investor/businessRules';

export type ChannelView = 'wholesale' | 'correspondent' | 'both';

export interface RosterStatistics {
  totalInvestors: number;
  totalApprovedWholesale: number;
  totalApprovedCorrespondent: number;
  productTypeBreakdown: Record<string, number>;
  statusBreakdown: { Approved: number; Pending: number; Inactive: number };
  correspondentBreakdown: { Approved: number; Pending: number; Inactive: number };
}

/**
 * All investors are visible to every role; the status filter controls (which now
 * default to Approved/Pending/Inactive) drive which rows show — there is no hidden
 * data-layer exclusion of Inactive investors. The `readOnly` arg is retained for
 * call-site compatibility. (Round-3 item 7.)
 */
export function getVisibleInvestors(investors: Investor[], _readOnly: boolean): Investor[] {
  return investors;
}

/** Aggregate stat-card + distribution counts. */
export function computeStatistics(visibleInvestors: Investor[]): RosterStatistics {
  if (!Array.isArray(visibleInvestors)) {
    return {
      totalInvestors: 0,
      totalApprovedWholesale: 0,
      totalApprovedCorrespondent: 0,
      productTypeBreakdown: {},
      statusBreakdown: { Approved: 0, Pending: 0, Inactive: 0 },
      correspondentBreakdown: { Approved: 0, Pending: 0, Inactive: 0 }
    };
  }

  const totalInvestors = visibleInvestors.length;
  const totalApprovedWholesale = visibleInvestors.filter(
    (inv) => inv.canDoWholesale && inv.status === 'Approved'
  ).length;
  const totalApprovedCorrespondent = visibleInvestors.filter(
    (inv) => inv.canGoCorrespondent && inv.correspondentStatus === 'Approved'
  ).length;

  const productTypeBreakdown: Record<string, number> = {};
  visibleInvestors.forEach((investor) => {
    getInvestorProducts(investor).forEach((type) => {
      productTypeBreakdown[type] = (productTypeBreakdown[type] || 0) + 1;
    });
  });

  const statusBreakdown = { Approved: 0, Pending: 0, Inactive: 0 };
  visibleInvestors.forEach((investor) => {
    statusBreakdown[investor.status as keyof typeof statusBreakdown]++;
  });

  const correspondentBreakdown = { Approved: 0, Pending: 0, Inactive: 0 };
  visibleInvestors
    .filter((inv) => inv.canGoCorrespondent)
    .forEach((investor) => {
      correspondentBreakdown[investor.correspondentStatus as keyof typeof correspondentBreakdown]++;
    });

  return {
    totalInvestors,
    totalApprovedWholesale,
    totalApprovedCorrespondent,
    productTypeBreakdown,
    statusBreakdown,
    correspondentBreakdown
  };
}

/**
 * Channel-agnostic delegation value from the authoritative `delegation` shape
 * (item 6), falling back to the legacy `delegatedType` for un-migrated rows.
 * Null when nothing is configured.
 */
export function deriveDelegationValue(investor: Investor): 'Delegated' | 'Non-Delegated' | 'Mixed' | null {
  const del = investor.delegation ?? { delegated: [], nonDelegated: [] };
  const hasDel = del.delegated.length > 0;
  const hasNon = del.nonDelegated.length > 0;
  if (hasDel && hasNon) {
    return 'Mixed';
  }
  if (hasDel) {
    return 'Delegated';
  }
  if (hasNon) {
    return 'Non-Delegated';
  }
  if (investor.delegatedType === 'Delegated/Non-Delegated') {
    return 'Mixed';
  }
  return investor.delegatedType || null;
}

/**
 * Roster "Delegation Level" label derived from the authoritative `delegation` shape
 * (item 6), falling back to the legacy `delegatedType` for un-migrated rows.
 *   wholesale-only-approved -> "Brokered" · all delegated -> "Delegated" ·
 *   all non-delegated -> "Non-Delegated" · mix -> "Mixed" · nothing -> "Not set".
 */
export function deriveDelegationLabel(investor: Investor): string {
  if (
    investor.canDoWholesale &&
    investor.status === 'Approved' &&
    (!investor.canGoCorrespondent || investor.correspondentStatus !== 'Approved')
  ) {
    return 'Brokered';
  }
  return deriveDelegationValue(investor) ?? 'Not set';
}

/**
 * Which channel "versions" of an investor are visible given the active per-channel status
 * filters (Round 6). A channel is visible iff the investor participates in it AND its status
 * passes that channel's filter (empty list = all, mirroring `filterInvestors`). Used by the
 * roster Both/card views so an Inactive channel is hidden by default and reappears when the
 * user selects Inactive in that channel's status pill.
 */
export function getVisibleChannels(
  investor: Investor,
  filters: Pick<InvestorFilters, 'wholesaleStatus' | 'correspondentStatus'>
): { wholesale: boolean; correspondent: boolean } {
  const passes = (list: string[], status: string) => list.length === 0 || list.includes(status);
  return {
    wholesale: Boolean(investor.canDoWholesale) && passes(filters.wholesaleStatus, investor.status),
    correspondent: Boolean(investor.canGoCorrespondent) && passes(filters.correspondentStatus, investor.correspondentStatus)
  };
}

/**
 * The full roster filter predicate, including the channel-aware status / OB /
 * date / net-worth rules.
 */
export function filterInvestors(
  visibleInvestors: Investor[],
  filters: InvestorFilters,
  channelView: ChannelView
): Investor[] {
  return visibleInvestors.filter((investor) => {
    const matchesSearch =
      filters.search === '' ||
      investor.investor.toLowerCase().includes(filters.search.toLowerCase()) ||
      investor.aeName.toLowerCase().includes(filters.search.toLowerCase()) ||
      investor.aeEmail.toLowerCase().includes(filters.search.toLowerCase()) ||
      (investor.wholesaleAeName && investor.wholesaleAeName.toLowerCase().includes(filters.search.toLowerCase())) ||
      (investor.wholesaleAeEmail && investor.wholesaleAeEmail.toLowerCase().includes(filters.search.toLowerCase())) ||
      (investor.correspondentAeName && investor.correspondentAeName.toLowerCase().includes(filters.search.toLowerCase())) ||
      (investor.correspondentAeEmail && investor.correspondentAeEmail.toLowerCase().includes(filters.search.toLowerCase()));

    const matchesStatus = filters.status === 'All' || investor.status === filters.status;
    const matchesProductType = filters.productType === '' || getInvestorProducts(investor).some((product) => product === filters.productType);

    const matchesChannelCapability = (() => {
      if (channelView === 'wholesale') {
        return investor.canDoWholesale === true;
      }
      if (channelView === 'correspondent') {
        return investor.canGoCorrespondent === true;
      }
      return true;
    })();

    const matchesDelegatedType =
      channelView !== 'correspondent' ||
      filters.delegatedType === 'All' ||
      deriveDelegationLabel(investor) === (filters.delegatedType === 'Delegated/Non-Delegated' ? 'Mixed' : filters.delegatedType);

    const wholesaleStatusMatches =
      !investor.canDoWholesale ||
      filters.wholesaleStatus.length === 0 ||
      filters.wholesaleStatus.includes(investor.status);

    const correspondentStatusMatches =
      !investor.canGoCorrespondent ||
      filters.correspondentStatus.length === 0 ||
      filters.correspondentStatus.includes(investor.correspondentStatus);

    const matchesChannelStatus =
      channelView === 'wholesale'
        ? wholesaleStatusMatches
        : channelView === 'correspondent'
        ? correspondentStatusMatches
        : (investor.canDoWholesale && wholesaleStatusMatches) || (investor.canGoCorrespondent && correspondentStatusMatches);

    const matchesWholesaleObIntegrated = (() => {
      if (channelView === 'correspondent' || !investor.canDoWholesale) {
        return true;
      }
      if (filters.wholesaleObIntegrated === 'All') {
        return true;
      }
      const isIntegrated = investor.wholesaleHasIntegratedOptimalBlue === true;
      return filters.wholesaleObIntegrated === 'Yes' ? isIntegrated : !isIntegrated;
    })();

    const matchesCorrespondentObIntegrated = (() => {
      if (channelView === 'wholesale' || !investor.canGoCorrespondent) {
        return true;
      }
      if (filters.correspondentObIntegrated === 'All') {
        return true;
      }
      const isIntegrated = investor.correspondentHasIntegratedOptimalBlue === true;
      return filters.correspondentObIntegrated === 'Yes' ? isIntegrated : !isIntegrated;
    })();

    const matchesApprovedDate = (() => {
      if (channelView === 'correspondent' || !investor.canDoWholesale) {
        return true;
      }
      if (!filters.approvedDateRange.startDate && !filters.approvedDateRange.endDate) {
        return true;
      }
      if (!investor.approvedAsOfDate) {
        return false;
      }
      const investorDate = parseAsLocalDate(investor.approvedAsOfDate);
      const startDate = filters.approvedDateRange.startDate ? parseAsLocalDate(filters.approvedDateRange.startDate) : null;
      const endDate = filters.approvedDateRange.endDate ? parseAsLocalDate(filters.approvedDateRange.endDate) : null;
      if (startDate && investorDate < startDate) {
        return false;
      }
      if (endDate && investorDate > endDate) {
        return false;
      }
      return true;
    })();

    const matchesCorrespondentApprovedDate = (() => {
      if (channelView === 'wholesale' || !investor.canGoCorrespondent) {
        return true;
      }
      if (!filters.correspondentApprovedDateRange.startDate && !filters.correspondentApprovedDateRange.endDate) {
        return true;
      }
      if (!investor.correspondentApprovedAsOfDate) {
        return false;
      }
      const investorDate = parseAsLocalDate(investor.correspondentApprovedAsOfDate);
      const startDate = filters.correspondentApprovedDateRange.startDate
        ? parseAsLocalDate(filters.correspondentApprovedDateRange.startDate)
        : null;
      const endDate = filters.correspondentApprovedDateRange.endDate
        ? parseAsLocalDate(filters.correspondentApprovedDateRange.endDate)
        : null;
      if (startDate && investorDate < startDate) {
        return false;
      }
      if (endDate && investorDate > endDate) {
        return false;
      }
      return true;
    })();

    const matchesCreatedDate = (() => {
      if (!filters.createdDateRange.startDate && !filters.createdDateRange.endDate) {
        return true;
      }
      const investorDate = parseAsLocalDate(investor.createdAt);
      const startDate = filters.createdDateRange.startDate ? parseAsLocalDate(filters.createdDateRange.startDate) : null;
      const endDate = filters.createdDateRange.endDate ? parseAsLocalDate(filters.createdDateRange.endDate) : null;
      if (startDate && investorDate < startDate) {
        return false;
      }
      if (endDate && investorDate > endDate) {
        return false;
      }
      return true;
    })();

    const matchesNetWorth = (() => {
      if (!filters.netWorthMin && !filters.netWorthMax) {
        return true;
      }
      const delegated = investor.netWorthRequirements?.delegated || 0;
      const nonDelegated = investor.netWorthRequirements?.nonDelegated || 0;
      const maxNetWorth = Math.max(delegated, nonDelegated);
      const minFilter = filters.netWorthMin ? parseFloat(filters.netWorthMin) : 0;
      const maxFilter = filters.netWorthMax ? parseFloat(filters.netWorthMax) : Infinity;
      return maxNetWorth >= minFilter && maxNetWorth <= maxFilter;
    })();

    return (
      matchesChannelCapability &&
      matchesSearch &&
      matchesStatus &&
      matchesProductType &&
      matchesDelegatedType &&
      matchesChannelStatus &&
      matchesWholesaleObIntegrated &&
      matchesCorrespondentObIntegrated &&
      matchesApprovedDate &&
      matchesCorrespondentApprovedDate &&
      matchesCreatedDate &&
      matchesNetWorth
    );
  });
}

/** The full roster sort comparator. */
export function sortInvestors(
  filteredInvestors: Investor[],
  sortField: keyof Investor,
  sortDirection: 'asc' | 'desc',
  filters: InvestorFilters
): Investor[] {
  return [...filteredInvestors].sort((a, b) => {
    // When filtering by status, sort alphabetically by investor name
    if (filters.wholesaleStatus.length < 3 || filters.correspondentStatus.length < 3) {
      return a.investor.localeCompare(b.investor);
    }

    // When filtering by product type, sort by status priority then date
    if (filters.productType !== '') {
      const statusOrder = { Approved: 1, Pending: 2, Inactive: 3 };
      const aStatusPriority = statusOrder[a.status as keyof typeof statusOrder] || 4;
      const bStatusPriority = statusOrder[b.status as keyof typeof statusOrder] || 4;
      if (aStatusPriority !== bStatusPriority) {
        return aStatusPriority - bStatusPriority;
      }
      return a.investor.localeCompare(b.investor);
    }

    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortField === 'netWorthRequirements') {
      const aDelegated = a.netWorthRequirements?.delegated || 0;
      const aNonDelegated = a.netWorthRequirements?.nonDelegated || 0;
      const bDelegated = b.netWorthRequirements?.delegated || 0;
      const bNonDelegated = b.netWorthRequirements?.nonDelegated || 0;
      aValue = Math.max(aDelegated, aNonDelegated);
      bValue = Math.max(bDelegated, bNonDelegated);
    }

    if (sortField === 'updatedAt') {
      const aDate = parseAsLocalDate(a.updatedAt).getTime();
      const bDate = parseAsLocalDate(b.updatedAt).getTime();
      return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
    }

    if (sortField === 'status' || sortField === 'correspondentStatus') {
      const statusOrder = { Approved: 1, Pending: 2, Inactive: 3 };
      const aStatusPriority = statusOrder[aValue as keyof typeof statusOrder] || 4;
      const bStatusPriority = statusOrder[bValue as keyof typeof statusOrder] || 4;
      if (aStatusPriority !== bStatusPriority) {
        return sortDirection === 'asc' ? aStatusPriority - bStatusPriority : bStatusPriority - aStatusPriority;
      }
      return 0;
    }

    if (sortField === 'wholesaleLenderPaidComp') {
      const aComp = a.wholesaleLenderPaidComp || 0;
      const bComp = b.wholesaleLenderPaidComp || 0;
      return sortDirection === 'asc' ? aComp - bComp : bComp - aComp;
    }

    if (sortField === 'wholesaleAdditionalLendwiseFee') {
      const aFee = a.wholesaleAdditionalLendwiseFee || 0;
      const bFee = b.wholesaleAdditionalLendwiseFee || 0;
      return sortDirection === 'asc' ? aFee - bFee : bFee - aFee;
    }

    if (sortField === 'wholesaleHasIntegratedOptimalBlue' || sortField === 'correspondentHasIntegratedOptimalBlue') {
      const aBool = aValue === true ? 1 : 0;
      const bBool = bValue === true ? 1 : 0;
      return sortDirection === 'asc' ? aBool - bBool : bBool - aBool;
    }

    if (sortField === 'delegatedType') {
      const aLabel = deriveDelegationLabel(a);
      const bLabel = deriveDelegationLabel(b);
      return sortDirection === 'asc' ? aLabel.localeCompare(bLabel) : bLabel.localeCompare(aLabel);
    }

    if (
      sortField === 'wholesaleEpoRule' ||
      sortField === 'wholesaleEpdRule' ||
      sortField === 'correspondentEpoRule' ||
      sortField === 'correspondentEpdRule'
    ) {
      const aRule = aValue as EpoRule | EpdRule | undefined;
      const bRule = bValue as EpoRule | EpdRule | undefined;
      const aRuleValue = aRule?.value ?? -1;
      const bRuleValue = bRule?.value ?? -1;
      return sortDirection === 'asc' ? aRuleValue - bRuleValue : bRuleValue - aRuleValue;
    }

    if (sortField === 'approvedAsOfDate' || sortField === 'correspondentApprovedAsOfDate') {
      const statusField = sortField === 'approvedAsOfDate' ? 'status' : 'correspondentStatus';
      const aStatus = a[statusField];
      const bStatus = b[statusField];
      const statusOrder = { Approved: 1, Pending: 2, Inactive: 3 };
      const aStatusPriority = statusOrder[aStatus as keyof typeof statusOrder] || 4;
      const bStatusPriority = statusOrder[bStatus as keyof typeof statusOrder] || 4;
      if (aStatusPriority !== bStatusPriority) {
        return aStatusPriority - bStatusPriority;
      }
      if (aStatus === 'Approved' && bStatus === 'Approved') {
        const aDate = aValue ? parseAsLocalDate(aValue as string).getTime() : 0;
        const bDate = bValue ? parseAsLocalDate(bValue as string).getTime() : 0;
        if (aDate && bDate) {
          return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
        }
        if (aDate && !bDate) {
          return -1;
        }
        if (!aDate && bDate) {
          return 1;
        }
      }
      return 0;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });
}

/**
 * EPO/EPD period → short display string. Mirrors the legacy `formatPeriod`
 * (which returns JSX); this returns a plain string with the value, suitable for
 * the v2 table where the numeric part is bolded by the caller.
 */
export function formatPeriodLabel(rule: EpoRule | EpdRule | undefined): { value: number; prefix?: string; suffix: string } | null {
  // Unconfigured rules (undefined, the value:0 placeholder, or any non-positive /
  // non-integer value) have no period to show — render nothing, not "0 payments".
  if (!rule || !isRuleConfigured(rule)) {
    return null;
  }
  const value = Number(rule.value);
  if (rule.type === 'payments') {
    return { value, suffix: 'payments to investor' };
  }
  if (rule.type === 'days_since_funding') {
    return { value, suffix: 'days after funding' };
  }
  if (rule.type === 'days_since_purchase') {
    return { value, suffix: 'days after purchase' };
  }
  if (rule.type === 'calendar_month_end_after_purchase') {
    return { value, prefix: 'through end of month ', suffix: 'after purchase' };
  }
  return null;
}
